import { useEffect, useState } from "react";
import { CircleCheckBig, ChevronDown, Loader2, Maximize2, MessageSquare, Minimize2, X } from "lucide-react";
import { useMutation } from "@tanstack/react-query";

import {
  downloadApplicationDraft,
  fetchApplicationDraft,
  generateGrantApplicationMarkdown,
  saveApplicationDraft,
  updateApplicationDraft,
} from "@/api/grant_application_drafts";
import type { GrantArtifactGeneration, GrantArtifactType } from "@/api/grant_artifacts";
import {
  GRANT_ARTIFACT_TABULAR_TYPES,
  GRANT_ARTIFACT_TYPE_LABELS,
  createGrantArtifactGeneration,
  downloadGrantArtifact,
  fetchGrantArtifact,
  getGrantArtifactGeneration,
  saveGrantArtifact,
  updateGrantArtifact,
} from "@/api/grant_artifacts";
import type {
  GrantDraftExportFormat,
  GrantOpportunityDocumentLink,
} from "@/api/grant_opportunity_documents";
import RichTextEditor from "@/components/editor/RichTextEditor";
import { DocumentCommentsPanel } from "@/components/file_viewer/DocumentCommentsPanel";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";

const EXPORT_FORMAT_LABELS: Record<GrantDraftExportFormat, string> = {
  docx: "Word (.docx)",
  pdf: "PDF (.pdf)",
  csv: "CSV (line items)",
};

const GENERATION_POLL_INTERVAL_MS = 1500;
const GENERATION_FLAVOR_INTERVAL_MS = 6000;

/** Rotating sub-copy shown while the model drafts (the long stage — a minute or more). */
const GENERATION_FLAVOR: Record<GrantArtifactType, string[]> = {
  budget: [
    "Summarizing total project costs…",
    "Building line items by category…",
    "Writing the budget narrative…",
    "Checking match and cost share…",
  ],
  progress_report: [
    "Summarizing project status…",
    "Listing completed activities…",
    "Assessing milestones and goals…",
    "Outlining next period's activities…",
  ],
  use_of_funds: [
    "Outlining funding allocations…",
    "Mapping the spending timeline…",
    "Documenting financial controls…",
  ],
};

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function ArtifactGenerationProgress({
  artifactType,
  generation,
}: {
  artifactType: GrantArtifactType;
  generation: GrantArtifactGeneration | null;
}) {
  const drafting = generation?.current_stage === "llm_generate";
  const flavorLines = GENERATION_FLAVOR[artifactType];
  const [flavorIndex, setFlavorIndex] = useState(0);

  useEffect(() => {
    if (!drafting) return;
    const timer = setInterval(
      () => setFlavorIndex((index) => (index + 1) % flavorLines.length),
      GENERATION_FLAVOR_INTERVAL_MS,
    );
    return () => clearInterval(timer);
  }, [drafting, flavorLines.length]);

  const steps = [
    { label: "Gathering grant context", active: !drafting, completed: drafting },
    {
      label: `Drafting your ${GRANT_ARTIFACT_TYPE_LABELS[artifactType].toLowerCase()}`,
      active: drafting,
      completed: false,
    },
  ];

  return (
    <div
      className="flex min-h-[240px] flex-1 flex-col items-center justify-center gap-3 text-sm text-muted-foreground"
      data-testid="artifact-generation-progress"
    >
      <div className="space-y-2">
        {steps.map((step) => (
          <div key={step.label} className="flex items-start gap-3">
            <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
              {step.completed ? (
                <CircleCheckBig className="h-4 w-4 text-green-500" strokeWidth={2.5} />
              ) : step.active ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30" />
              )}
            </div>
            <div>
              <span className={step.active ? "font-medium text-foreground" : undefined}>
                {step.label}
                {step.active ? "…" : ""}
              </span>
              {step.active && drafting ? (
                <p className="mt-0.5 text-xs text-muted-foreground/80">
                  {flavorLines[flavorIndex]} This usually takes a minute or two.
                </p>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

type EditorMode = "generate" | "edit";

type GrantApplicationDraftEditorProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  municipalityId: number;
  opportunityId: number;
  opportunityTitle: string;
  mode: EditorMode;
  grantId?: number | null;
  linkId?: number | null;
  /** When set, the editor generates/saves a typed artifact draft instead of an application draft. */
  artifactType?: GrantArtifactType | null;
  onSaved?: (link: GrantOpportunityDocumentLink) => void;
};

export function GrantApplicationDraftEditor({
  open,
  onOpenChange,
  municipalityId,
  opportunityId,
  opportunityTitle,
  mode,
  grantId,
  linkId,
  artifactType,
  onSaved,
}: GrantApplicationDraftEditorProps) {
  const { toast } = useToast();
  const [markdown, setMarkdown] = useState("");
  const [savedMarkdown, setSavedMarkdown] = useState<string | null>(null);
  const [savedLinkId, setSavedLinkId] = useState<number | null>(linkId ?? null);
  const [documentId, setDocumentId] = useState<number | null>(null);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [generation, setGeneration] = useState<GrantArtifactGeneration | null>(null);

  const draftNoun = artifactType
    ? `${GRANT_ARTIFACT_TYPE_LABELS[artifactType]} draft`
    : "Application draft";
  const draftLabel = `${draftNoun} — ${opportunityTitle}`;
  const effectiveLinkId = savedLinkId ?? linkId ?? null;

  useEffect(() => {
    if (!open) {
      setFullscreen(false);
      setCommentsOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    setLoadError(null);
    setSavedLinkId(linkId ?? null);
    setDocumentId(null);
    setMarkdown("");
    setSavedMarkdown(null);
    setGeneration(null);

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        if (mode === "edit" && linkId) {
          const draft = artifactType
            ? await fetchGrantArtifact(municipalityId, opportunityId, linkId)
            : await fetchApplicationDraft(municipalityId, opportunityId, linkId);
          if (!cancelled) {
            setMarkdown(draft.markdown);
            setSavedMarkdown(draft.markdown);
            setDocumentId(draft.document_id);
          }
        } else if (mode === "generate" && artifactType) {
          // Drafting runs in a background job (LLM latency exceeds request
          // timeouts, ENG-1707); poll until it settles. The server reuses an
          // in-flight generation, so reopening the dialog reattaches.
          let current = await createGrantArtifactGeneration(
            municipalityId,
            opportunityId,
            artifactType,
          );
          if (!cancelled) setGeneration(current);
          while (!cancelled && (current.status === "pending" || current.status === "processing")) {
            await sleep(GENERATION_POLL_INTERVAL_MS);
            if (cancelled) break;
            current = await getGrantArtifactGeneration(municipalityId, opportunityId, current.id);
            if (!cancelled) setGeneration(current);
          }
          if (!cancelled) {
            if (current.status === "failed") {
              throw new Error(current.error_message ?? "Could not generate artifact draft");
            }
            setMarkdown(current.markdown ?? "");
          }
        } else if (mode === "generate" && grantId) {
          const generated = await generateGrantApplicationMarkdown(grantId, municipalityId);
          if (!cancelled) setMarkdown(generated);
        } else {
          throw new Error("Missing grant or draft to load");
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : "Could not load draft");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [open, mode, grantId, linkId, artifactType, municipalityId, opportunityId]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const snapshot = markdown;
      const payload = { markdown: snapshot, label: draftLabel };
      let link: GrantOpportunityDocumentLink;
      if (effectiveLinkId) {
        link = artifactType
          ? await updateGrantArtifact(municipalityId, opportunityId, effectiveLinkId, payload)
          : await updateApplicationDraft(municipalityId, opportunityId, effectiveLinkId, payload);
      } else {
        link = artifactType
          ? await saveGrantArtifact(municipalityId, opportunityId, artifactType, payload)
          : await saveApplicationDraft(municipalityId, opportunityId, payload);
      }
      return { link, snapshot };
    },
    onSuccess: ({ link, snapshot }) => {
      setSavedLinkId(link.id);
      setDocumentId(link.document_id);
      setSavedMarkdown(snapshot);
      onSaved?.(link);
      toast({
        title: effectiveLinkId ? `${draftNoun} updated` : `${draftNoun} saved`,
        description: link.label ?? link.name,
      });
    },
    onError: (error) => {
      toast({
        title: `Could not save ${draftNoun.toLowerCase()}`,
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive",
      });
    },
  });

  const hasUnsavedChanges = savedMarkdown === null || markdown !== savedMarkdown;

  const exportMutation = useMutation({
    mutationFn: async (format: GrantDraftExportFormat) => {
      let targetLinkId = effectiveLinkId;
      // The export endpoint reads the saved draft, so persist first: a
      // never-saved draft is created in the Grants folder, and unsaved edits
      // are flushed to keep the download in sync with the editor.
      if (!targetLinkId || hasUnsavedChanges) {
        const { link } = await saveMutation.mutateAsync();
        targetLinkId = link.id;
      }
      if (artifactType) {
        await downloadGrantArtifact(municipalityId, opportunityId, targetLinkId, format);
      } else {
        await downloadApplicationDraft(municipalityId, opportunityId, targetLinkId, format);
      }
      return format;
    },
    onSuccess: (format) => {
      toast({
        title: `${draftNoun} exported`,
        description: `Downloaded as ${EXPORT_FORMAT_LABELS[format]} for review.`,
      });
    },
    onError: (error) => {
      toast({
        title: `Could not export ${draftNoun.toLowerCase()}`,
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive",
      });
    },
  });

  const commentsActive = commentsOpen && documentId != null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className={cn(
          "flex flex-col gap-0 overflow-hidden p-0",
          fullscreen
            ? "fixed inset-3 z-50 flex h-[calc(100vh-1.5rem)] max-h-none w-[calc(100vw-1.5rem)] max-w-none translate-x-0 translate-y-0 sm:rounded-xl"
            : commentsActive
              ? "max-h-[85vh] max-w-5xl"
              : "max-h-[85vh] max-w-3xl",
        )}
        data-testid="grant-application-draft-editor"
      >
        <div className="relative shrink-0 border-b px-6 py-4">
          <div className="absolute right-4 top-4 flex items-center gap-1">
            {!loading && !loadError ? (
              <>
                <Button
                  type="button"
                  variant={commentsActive ? "secondary" : "outline"}
                  size="icon"
                  className="h-8 w-8"
                  disabled={documentId == null}
                  aria-label={
                    documentId == null
                      ? "Team comments unavailable until draft is saved"
                      : commentsOpen
                        ? "Hide team comments"
                        : "Show team comments"
                  }
                  title={
                    documentId == null ? "Save to File Center to enable team comments" : undefined
                  }
                  onClick={() => setCommentsOpen((value) => !value)}
                >
                  <MessageSquare className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  aria-label={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                  onClick={() => setFullscreen((value) => !value)}
                >
                  {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
              </>
            ) : null}
            <DialogClose asChild>
              <Button type="button" variant="outline" size="icon" className="h-8 w-8" aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            </DialogClose>
          </div>
          <DialogHeader className="space-y-1 pr-28 text-left">
            <DialogTitle>
              {mode === "generate"
                ? `Review ${draftNoun.toLowerCase()}`
                : `Edit ${draftNoun.toLowerCase()}`}
            </DialogTitle>
            <DialogDescription>
              Format sections and lists as needed. Expand for a focused writing view.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="flex min-h-0 flex-1 overflow-hidden">
          <div
            className={cn(
              "flex min-h-0 min-w-0 flex-1 flex-col",
              commentsActive ? "min-h-0 pl-4 pr-3 pb-4" : "px-6 pb-2 pt-0",
            )}
          >
            {loading && mode === "generate" && artifactType ? (
              <ArtifactGenerationProgress artifactType={artifactType} generation={generation} />
            ) : loading ? (
              <div className="flex min-h-[240px] flex-1 items-center justify-center text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {mode === "generate" ? "Generating draft…" : "Loading draft…"}
              </div>
            ) : loadError ? (
              <p className="text-sm text-destructive">{loadError}</p>
            ) : (
              <div className="flex min-h-0 flex-1 flex-col">
                <RichTextEditor
                  initialMarkdown={markdown}
                  onChangeMarkdown={setMarkdown}
                  className="min-h-0 flex-1"
                  toolbarMode="document"
                  flush={commentsActive}
                  editorMinHeightClass={fullscreen ? "min-h-[calc(100vh-16rem)]" : "min-h-[280px]"}
                />
              </div>
            )}
          </div>

          {commentsActive && documentId != null ? (
            <div className="flex w-80 shrink-0 flex-col overflow-hidden border-l border-border/80 bg-background">
              <DocumentCommentsPanel
                municipalityId={municipalityId}
                documentId={documentId}
                opportunityId={opportunityId}
                embedded
                className="h-full min-h-0"
              />
            </div>
          ) : null}
        </div>

        <DialogFooter className="shrink-0 border-t px-6 py-4 sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {/* The menu must be modal: its focus scope stacks on top of the
              dialog's, pausing the dialog's focus trap while open. Non-modal,
              the trap yanks focus back from the portaled menu and it
              self-dismisses on open (ENG-1686). */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                disabled={
                  loading ||
                  Boolean(loadError) ||
                  exportMutation.isPending ||
                  saveMutation.isPending
                }
                title={
                  !effectiveLinkId
                    ? "Saves to File Center, then downloads"
                    : hasUnsavedChanges
                      ? "Saves your changes, then downloads"
                      : undefined
                }
              >
                {exportMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Exporting…
                  </>
                ) : (
                  <>
                    Export
                    <ChevronDown className="ml-1.5 h-4 w-4" />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            {/* The dialog's modal lock sets pointer-events: none on <body>; the
                menu portals outside the dialog content, so restore pointer
                events explicitly (ENG-1702). */}
            <DropdownMenuContent align="end" className="pointer-events-auto">
              <DropdownMenuItem onSelect={() => exportMutation.mutate("docx")}>
                {EXPORT_FORMAT_LABELS.docx}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => exportMutation.mutate("pdf")}>
                {EXPORT_FORMAT_LABELS.pdf}
              </DropdownMenuItem>
              {artifactType && GRANT_ARTIFACT_TABULAR_TYPES.includes(artifactType) ? (
                <DropdownMenuItem onSelect={() => exportMutation.mutate("csv")}>
                  {EXPORT_FORMAT_LABELS.csv}
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            disabled={loading || Boolean(loadError) || !markdown.trim() || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : effectiveLinkId ? (
              "Save changes"
            ) : (
              "Save to File Center"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
